import { Button, Card, Modal } from "react-bootstrap"
import useUserRefs from "../../Hooks/UseUsersQuery/UseUserRefs"
import { useAddNewDataLogMutation, useDeleteDataLogMutation, useGetDataLogsQuery } from "../../services/bClientApi"
import timeService from "../../services/time/timeService"
import { convertStringToJSON } from "../../Helpers/Strings/stringHelpers"
import { useState } from "react"
import QuickForm from "../Forms/QuickForm/QuickForm"
import { accountSelector } from "../../redux/account/accountSlice"
import { useSelector } from "react-redux"
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook"
import { ConfirmModal } from "../Modals/confirmModal"
import canAccess from "../../services/accessService/accessService"
import MongoPagination from "../Pagination/MongoPagination"
import DataLogDiffs from "./DataLogDiffs"

export default function DataLogList(props: {
    dataType: string,
    dataId: string,
}) {

    const { myProperties } = useMyProperties()

    const baseUserInfo = useSelector(accountSelector)
    const { _id } = baseUserInfo

    const userRef = useUserRefs()

    const [loadData, setLoadData] = useState(false)
    const [addNoteMode, setAddNoteMode] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        active: boolean,
        dataLogId: string,
        title: string,
        message: string
    }>({
        active: false,
        dataLogId: '',
        title: '',
        message: ''
    })

    const {
        dataType,
        dataId,
    } = props

    const [
        addNewDataLog,
    ] = useAddNewDataLogMutation()

    const [
        deleteDataLog,
        { isLoading: deleteLoading }
    ] = useDeleteDataLogMutation()

    const {
        data,
        error,
        isLoading,
        isFetching,
    } = useGetDataLogsQuery({
        dataLogsQuery: {
            dataType,
            dataId,
        },
        pagination: {
            limit,
            page,
        }
    }, { skip: !dataType || !dataId || !loadData })

    const dataLogList = data?.docs || []

    const handleAddNote = async (note: string) => {
        setAddNoteMode(false)
        const newNoteEntry = {
            note,
            dataType,
            dataId,
            logType: 'Note',
            createdDate: new Date(),
            createdBy: _id ? _id : 'unidentified',
            data: '{}',
        }

        await addNewDataLog(newNoteEntry).unwrap()
    }

    const resetDeleteConfirmation = () => {
        setDeleteConfirmation({
            active: false,
            dataLogId: '',
            title: '',
            message: ''
        })
    }

    const handleDeleteDataLog = async () => {
        try {
            await deleteDataLog(deleteConfirmation.dataLogId).unwrap()
            resetDeleteConfirmation()
        } catch (error) {
            console.error('Error deleting data log:', error)
        }
    }

    if (!loadData) {
        return (
            <div className="text-end">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        setLoadData(true)
                    }}
                >
                    Show Data Log
                </Button>

            </div>
        )
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center">
                <h3>
                    Data Log
                </h3>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        setLoadData(false)
                        setPage(1)
                    }}
                >
                    Hide Data Log
                </Button>
            </div>
            <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                    setAddNoteMode(true)
                }}
                className="mb-2"
            >
                Add Note
            </Button>

            {isLoading && <p>Loading...</p>}
            {error && <p>Error: {error}</p>}
            {isFetching && <p>Fetching...</p>}

            {!isLoading && !isFetching && !error && dataLogList.length === 0 && (
                <p>No entries found...</p>
            )}

            {dataLogList.map((dataLogEntry: any, idx: number) => {
                const {
                    _id: dataLogId,
                    createdBy,
                    createdDate,
                    // dataId,
                    dataType,
                    logType,
                    note,
                    data,
                } = dataLogEntry

                const parsedData = convertStringToJSON(data)

                return (
                    <Card key={idx} className="mb-2 small p-2" style={{ position: 'relative' }}>
                        {canAccess("dataLogs", "deleteLog") && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => {
                                    setDeleteConfirmation({
                                        active: true,
                                        dataLogId: dataLogId,
                                        title: 'Delete Data Log Entry',
                                        message: 'Are you sure you want to delete this data log entry? This action cannot be undone.'
                                    })
                                }}
                                disabled={deleteLoading}
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    padding: '0',
                                    color: '#999',
                                    fontSize: '18px',
                                    lineHeight: '1',
                                    textDecoration: 'none'
                                }}
                                className="hover-danger"
                            >
                                ×
                            </Button>
                        )}
                        <Card.Body className="p-2">
                            <p className="mb-2">
                                <span className="fst-italic">
                                    {timeService.getFormattedTime(createdDate)}
                                </span>

                                <br />
                                <b>

                                    {userRef && userRef[createdBy] ?
                                        (<>
                                            {userRef[createdBy].firstName + ' ' + userRef[createdBy].lastName}
                                        </>) : (
                                            <>
                                                {createdBy}
                                            </>
                                        )}
                                </b>
                                <br />
                                <span className="text-muted text-capitalize">
                                    {dataType} {logType}
                                </span>
                                <br />
                            </p>
                            {note && (
                                <p className="fst-italic keep-whitespace mb-2">
                                    {note}
                                </p>
                            )}

                            {parsedData?.diffs && (
                                <DataLogDiffs
                                    diffs={parsedData.diffs}
                                    dataType={dataType}
                                    myProperties={myProperties}
                                />
                            )}
                        </Card.Body>
                    </Card>
                )
            })}

            {data && (
                <MongoPagination
                    data={data}
                    loadPage={(q: { page?: number | string, limit?: number | string }) => {
                        if (q.limit) setLimit(Number(q.limit))
                        if (q.page) setPage(Number(q.page))
                    }}
                />
            )}

            <Modal
                show={addNoteMode}
                onHide={() => {
                    setAddNoteMode(false)
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Add Note
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-lg font-semibold">
                        Enter your note below.
                    </p>

                    <QuickForm
                        initialData={{
                            note: '',
                        }}
                        fields={[
                            {
                                fieldLabel: 'Note',
                                fieldName: 'note',
                                fieldType: 'textarea',
                                required: true,
                            }
                        ]}
                        onSubmit={(formData) => {
                            console.log('Form data:', formData)
                            handleAddNote(formData.note)
                        }}
                        onCancel={() => {
                            setAddNoteMode(false)
                        }}
                    />

                </Modal.Body>
                {/* <Modal.Footer>
                    <Button variant="primary" onClick={() => { }}>
                        Confirm
                    </Button>
                    <Button variant="secondary" onClick={() => { setAddNoteMode(false) }}>
                        Cancel
                    </Button>
                </Modal.Footer> */}

            </Modal>

            <ConfirmModal
                active={deleteConfirmation.active}
                title={deleteConfirmation.title}
                message={deleteConfirmation.message}
                onConfirm={handleDeleteDataLog}
                onClose={resetDeleteConfirmation}
                disabled={deleteLoading}
            />

        </div>
    )
}